<template lang="pug">
v-dialog.dialog(v-model="dialog" max-width="650px" :persistent="preventDialogClose")
  v-card(:data-type="type")
    v-icon.close-btn(v-if="closeIcon" size="18" @click="close()") mdi-close
    v-icon.alert-icon(size="32" @click="close()" v-if="alertIcon") mdi-alert-outline
    v-card-title.text-h5(v-if="title" :class="description ? 'padding-bottom':''" :data-type="type") {{ title }}
    v-card-text(v-if="warningIcon || heading || description" :data-type="type")
      div.alert-icon(v-if="warningIcon")
        v-icon(size="40") mdi-alert-circle-outline
      strong(v-html="heading")
      p.description(v-html="description")
      div(v-if="otpTitle")
        p.otp-title {{otpTitle}}
        div.otp-input-container
          v-otp-input(length=6 type='number' v-model='pin') 
      div.footer
        strong(v-html="footer")
    v-card-actions(v-if="showActionsButton" :data-type="type")
      v-btn(:data-info="isInfoDialog" @click="close()") {{ closeButtonText }}
      v-btn(@click="save()") {{ saveButtonText }}
        v-progress-circular.spinner(v-if="showProgress" indeterminate :color="'#fff'")
    v-card-actions(v-else-if="type === 'confirmNav'" :data-type="type")
      v-btn(@click="close()") Annuler
      v-btn(@click="proceed()") {{ closeButtonText }}
      v-btn(@click="save()") {{ saveButtonText }}
    v-card-actions(v-else :data-type="type" class="confirmButton")
      v-btn(@click="close()") {{ closeButtonText }}
</template>
<script setup lang="ts">
const emit = defineEmits(['on:save', 'on:close', 'on:proceed'])

const props = defineProps({
  /**
   * type is used to identify the modal type
   */
  type: {
    type: String,
    default() {
      return 'save'
    },
  },
  /**
   * title is used to display the modal title
   */
  title: {
    type: String,
    default() {
      return ''
    },
  },
  /**
   * pinValue is used to display the pin value
   */
  pinValue: {
    type: String,
    default() {
      return ''
    },
  },
  /**
   * title is used to display the otp title
   */
  otpTitle: {
    type: String,
    default() {
      return ''
    },
  },
  /**
   * heading is used to display the modal heading
   */
  heading: {
    type: String,
    default() {
      return ''
    },
  },
  /**
   * description is used to display the modal description
   */
  description: {
    type: String,
    default() {
      return ''
    },
  },
  /**
   * footer is used to display the modal footer text
   */
  footer: {
    type: String,
    default() {
      return ''
    },
  },
  /**
   * saveButtonText is used to display the save button text
   */
  saveButtonText: {
    type: String,
    default() {
      return 'Sauvegarder'
    },
  },
  /**
   * closeButtonText is used to display the close button text
   */
  closeButtonText: {
    type: String,
    default() {
      return 'Fermer'
    },
  },
  /**
   * showProgress is used to display spinner in button
   */
  showProgress: {
    type: Boolean,
    default() {
      return false
    },
  },
  /**
   * showDialog is used to show the dialog
   */
  showDialog: {
    type: Boolean,
    default() {
      return false
    },
  },
  /**
   * preventDialogClose is used to prevent the dialog close on esc key & outside mouse click event
   */
  preventDialogClose: {
    type: Boolean,
    default() {
      return false
    },
  },
  closeIcon: {
    type: Boolean,
    default() {
      return false
    },
  },
  warningIcon: {
    type: Boolean,
    default() {
      return false
    },
  },
  showActionsButton: {
    type: Boolean,
    default() {
      return true
    },
  },
  isInfoDialog: {
    type: Boolean,
    default() {
      return false
    },
  },
  alertIcon: {
    type: Boolean,
    default() {
      return false
    },
  },
})

const dialog: Ref<boolean> = ref(props.showDialog)
const pin = ref(props.pinValue)

watch(
  () => props.pinValue,
  (value, prevValue) => {
    pin.value = value
  },
)

watch(
  () => props.showDialog,
  (value, prevValue) => {
    dialog.value = value
  },
)

const save = () => {
  emit('on:save', pin.value)
}
const proceed = () => {
  emit('on:proceed')
}

const reset = () => {
  pin.value = ''
}

defineExpose({ reset })

const close = () => {
  emit('on:close')
}
</script>
<style lang="stylus" scoped>
.dialog
  .v-card
    position relative
    gap 5px
    padding 0 1rem
    color var(--japanese-indigo, #194355)
    border 1px solid var(--japanese-indigo, #194355)
    &[data-type="confirm"], &[data-type="docAI"]
        border-radius 8px
        padding 1rem
    .close-btn
      position absolute
      top 0
      right 0
      margin 16px
      color var(--japanese-indigo, #194355)
      background-color transparent
      &:hover
        background-color rgba(25, 67, 85, 0.1)
    .alert-icon
      margin-left auto
      margin-right auto
      margin-top 20px
      color #DD3148
  .v-card-text
    padding 25px 55px 10px 55px !important
    font-size 0.9rem !important
    text-align center
    &[data-type="confirmNav"]
      padding 22px 24px 9px 27px !important
      font-family var(--font-family-text-contrast)
    .alert-icon
      padding-bottom 20px
    .description
      margin-top 1rem
      font-weight 600
      text-align left

      :deep(ul)
        text-align left
        font-size 12px
        font-weight normal
        li
          padding-bottom 5px
    .footer
      font-size 0.875rem
      margin-top 10px
      font-weight 600
    .otp-title
      font-size 0.813rem
      font-weight 600
      padding-left 10px
      margin-top 30px
  .v-card-title
    padding 2rem 1rem !important
    text-align center
    font-weight 600
    &[data-type="confirmNav"]
      padding 2px 1rem !important
      font-family var(--font-family-text-contrast)
    &[data-type="alert"]
      color var(--maximum-red, #D81818)
  .v-card-actions
    justify-content center !important
    .v-btn
      color var(--white, #FFFFFF)
      width 48%
      letter-spacing 0 !important
      margin-bottom 0.6rem
      text-transform capitalize
      font-weight bold
      font-size 0.875rem
      padding 0 2.5rem
      &:nth-child(1)
        &[data-info="true"]
          display none
        border 1px solid var(--japanese-indigo, #194355)
        color var(--japanese-indigo, #194355)
      &:nth-child(2)
        background-color var(--japanese-indigo, #194355)
    &[data-type="confirmNav"]
      .v-btn
        width 40%
        font-family var(--font-family-text-contrast)
        font-weight 500
        font-size 14px
        &:nth-child(1)
          width 20%
          border none
          color var(--japanese-indigo, #194355)
        &:nth-child(2)
          background-color var(--cruel-ruby, #DD3148)
        &:nth-child(3)
          background-color var(--japanese-indigo, #194355)
          color var(--white, #FFFFFF)
    &[data-type="confirm"]
      .v-btn
        width 48%
    &[data-type="docAI"]
      .v-btn
        width 25%
        font-size 12px !important
    .spinner
      width 22px
      position absolute
      right var(--fs-8, 0.5rem)
  .padding-bottom
    padding 2rem 1rem 0 1rem !important
  .confirmButton
    justify-content center !important
    .v-btn
      background-color var(--japanese-indigo, #194355)
      color var(--white, #FFFFFF) !important
</style>
